// Utility to manage work sessions in localStorage

export interface WorkSession {
    id: string;
    date: string;
    duration?: number; // in minutes
    objectives: string[];
    notes: string;
    tasksCompleted: string[];
    conversationId?: string;
}

const STORAGE_KEY = 'reminiscence_work_sessions';

/**
 * Get all work sessions from localStorage
 */
export function getSessions(): WorkSession[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];
        return JSON.parse(stored);
    } catch (error) {
        console.error('Error reading sessions from localStorage:', error);
        return [];
    }
}

/**
 * Add a new work session
 */
export function addSession(session: Omit<WorkSession, 'id'>): WorkSession {
    const sessions = getSessions();
    const newSession: WorkSession = {
        ...session,
        id: generateId(),
    };
    sessions.unshift(newSession); // Add to beginning
    saveSessions(sessions);
    return newSession;
}

/**
 * Update an existing session
 */
export function updateSession(id: string, updates: Partial<WorkSession>): void {
    const sessions = getSessions();
    const index = sessions.findIndex(s => s.id === id);
    if (index !== -1) {
        sessions[index] = { ...sessions[index], ...updates };
        saveSessions(sessions);
    }
}

/**
 * Delete a session
 */
export function deleteSession(id: string): void {
    const sessions = getSessions().filter(s => s.id !== id);
    saveSessions(sessions);
}

/**
 * Get the most recent session
 */
export function getLatestSession(): WorkSession | null {
    const sessions = getSessions();
    return sessions.length > 0 ? sessions[0] : null;
}

/**
 * Export sessions as markdown
 */
export function exportSessionsAsMarkdown(): string {
    const sessions = getSessions();
    let markdown = '# Historique des Sessions de Travail - Réminiscence\n\n';

    sessions.forEach(session => {
        markdown += `## Session du ${new Date(session.date).toLocaleDateString('fr-FR')}\n\n`;

        if (session.duration) {
            markdown += `**Durée** : ${session.duration} minutes\n\n`;
        }

        if (session.conversationId) {
            markdown += `**Conversation ID** : \`${session.conversationId}\`\n\n`;
        }

        if (session.objectives.length > 0) {
            markdown += '### Objectifs\n\n';
            session.objectives.forEach(obj => {
                markdown += `- ${obj}\n`;
            });
            markdown += '\n';
        }

        if (session.tasksCompleted.length > 0) {
            markdown += '### Tâches Accomplies\n\n';
            session.tasksCompleted.forEach(task => {
                markdown += `- [x] ${task}\n`;
            });
            markdown += '\n';
        }

        if (session.notes) {
            markdown += '### Notes\n\n';
            markdown += `${session.notes}\n\n`;
        }

        markdown += '---\n\n';
    });

    return markdown;
}

/**
 * Save sessions to localStorage
 */
function saveSessions(sessions: WorkSession[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
        console.error('Error saving sessions to localStorage:', error);
    }
}

/**
 * Generate a unique ID
 */
function generateId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Clear all sessions (use with caution)
 */
export function clearAllSessions(): void {
    localStorage.removeItem(STORAGE_KEY);
}
